import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { BannerType } from '../../../../models/BannersModels';
import { DefaultTextboxLength } from '../../../../configs/RegexConfig';

import BannerTabs from '../../../../components/BannerTabs/BannerTabs';
import TextInput from '../../../../components/TextInput/TextInput';
import ClickableLink from '../../../../components/Text/ClickableLink';
import WarningIconTransparent from '../../../../components/Icons/WarningIconTransparent';
import { WarningContainer } from './BannerFormContainerStyle';
import useMessagePreview from '../../components/useMessagePreview';

interface MessagePreviewProps {
  bannerType: BannerType | '';
  startDate: DateTime | null;
  endDate: DateTime | null;
  messages: Record<string, string>;
  onMessageChange: (language: string, value: string) => void;
  touched?: boolean;
  onBlur?: () => void;
}

function MessagePreview({
  bannerType,
  startDate,
  endDate,
  messages = {},
  onMessageChange,
  touched,
  onBlur,
}: MessagePreviewProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const {
    languageTabs,
    activeLanguage,
    activeLanguageIndex,
    setActiveLanguageIndex,
    fixedT,
    templateKey,
    startDateText,
    endDateText,
  } = useMessagePreview({ bannerType, startDate, endDate });

  // Each language tab shares the same underlying formik field, so its error must be derived per-tab
  // here instead of relying on a single aggregated message from the schema (which can't say which
  // language it refers to)
  const activeMessage = messages[activeLanguage] ?? '';
  const isEnglishMissing = activeLanguage === 'en' && !activeMessage.trim();
  const isTooLong = activeMessage.length > DefaultTextboxLength;
  const error = touched && (isEnglishMissing || isTooLong);

  let feedback = '';
  if (isEnglishMissing) feedback = t('common.inputValidations.languageRequired');
  else if (isTooLong) feedback = t('common.inputValidations.maxLength', { len: DefaultTextboxLength });

  return (
    <Box sx={{ paddingBottom: '2.5rem' }}>
      <BannerTabs tabs={languageTabs} handleTabChange={setActiveLanguageIndex} />
      {bannerType === BannerType.OTHER ? (
        <>
          <TextInput
            name={`messages.${activeLanguage}`}
            placeholder={t('pages.bannerOnboarding.message.typePlaceholder')}
            type="text"
            multiline
            minRows={4}
            maxRows={4}
            value={messages[activeLanguage] ?? ''}
            onInputChange={(e) => onMessageChange(activeLanguage, e.target.value)}
            onBlur={onBlur}
            error={error}
            feedback={feedback}
            formStyle={{ marginTop: '0.5rem' }}
            inputStyle={{
              '& .MuiInputBase-input': {
                padding: '1rem',
              },
            }}
          />
          {!messages[activeLanguage] && activeLanguage !== 'en' ? (
            <WarningContainer>
              <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
                <WarningIconTransparent />
                <Typography sx={{ fontWeight: '700' }}>
                  {t('pages.bannerOnboarding.message.noTranslationAvailable', {
                    language: languageTabs[activeLanguageIndex],
                  })}
                </Typography>
              </Box>
            </WarningContainer>
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            marginTop: '0.5rem',
            minHeight: '8rem',
            padding: '1rem 1.25rem',
            border: `1px solid ${theme.palette.monochrome.monoChrome3}`,
            borderRadius: '0.5rem',
          }}
        >
          {templateKey ? (
            <Typography component="div" sx={{ fontSize: '1.125rem' }}>
              <Trans
                t={fixedT}
                i18nKey={`pages.bannerOnboarding.message.templates.${templateKey}`}
                values={{ startDate: startDateText, endDate: endDateText }}
                tOptions={{ interpolation: { escapeValue: false } }}
                components={{
                  Link: <ClickableLink to="https://portal.vodafone.com/web/portal/tickets" target="_blank" />,
                }}
              />
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.monochrome.monoChrome4 }}>
              {t('pages.bannerOnboarding.message.previewPlaceholder')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default MessagePreview;
